import moment from "moment";

import { ChartDataPoint } from "@/Patient/Models/GrowthCharts/ChartData";
import { VitalSignChartModel } from "./UIModels/ChartConfigurations/VitalSignChartModel";
import { PatientChartDataTableProps, PatientChartTable } from "./PatientChartTable";

interface VitalSignsDataTableProps extends PatientChartDataTableProps {
    chart: VitalSignChartModel;
    hightlightRow?: ChartDataPoint;
    onVitalSignClick: (medicalItemId: string) => void;
}

class VitalSignsDataTable extends PatientChartTable<VitalSignsDataTableProps, never> {
    public render(): JSX.Element {
        return (
            <div className="datatable datatable-corrected">
                <div
                    className={"datatable-wrapper " + (this.props.chart.isPlotSelectionSupported ? "chart-selector" : "no-chart-selector")}
                    ref={ref => (this.tableContainer = ref)}
                >
                    {this.renderTable()}
                </div>
            </div>
        );
    }

    protected renderTableHeader(): JSX.Element {
        // here we check if the secondary data table column is filled, if it is, we can assume
        // that we are in the BP vital sign, and we need to use the label set in the ILineChartConfig
        // and not the yAxis label
        const primaryLabel = this.props.chart.secondary?.label ? this.props.chart.primary.label : this.props.chart.yAxis.label;
        return (
            <thead>
                <tr>
                    <th rowSpan={2} colSpan={this.props.chart.secondary?.label ? 2 : 1}>{cacheStore.GetString("DateLabel")}</th>
                    <th colSpan={1} className={this.props.chart.secondary?.label ? "datatable-dashed-border-bottom pre-wrap" : "pre-wrap"}>{this.getColumnTitle(primaryLabel)}</th>
                    {this.props.chart.secondary?.label &&
                        <th colSpan={1} className="datatable-dashed-border-bottom pre-wrap">{this.getColumnTitle(this.props.chart.secondary?.label)}</th>
                    }
                </tr>
                {this.props.chart.secondary?.label && (
                    <tr>
                        <th className="datatable-dot datatable-dot-blue" />
                        {this.props.chart.secondary?.label &&
                            <th className="datatable-dot datatable-dot-orange" />
                        }
                    </tr>
                )}
            </thead>
        );
    }

    protected renderTableBody(): JSX.Element {
        const chartDataPrimaryPoints = this.props.chart.primary.dataPoints;
        const chartDataSecondaryPoints = this.props.chart.secondary?.dataPoints;

        if (!chartDataPrimaryPoints?.length)
            return <tbody />;

        return (
            <tbody>
                {chartDataPrimaryPoints.map(datarow => {
                    const date = moment(datarow.x);
                    return (
                        <tr
                            key={datarow.id}
                            ref={element => this.tableRows[datarow.id] = element}
                            className={this.getRowClass(datarow.id)}
                            onClick={() => this.props.onVitalSignClick(datarow.medicalItemId)}
                        >
                            <td colSpan={this.props.chart.secondary?.label ? 2 : 1}>{moment(date).format("YYYY-MM-DD")}</td>
                            <td colSpan={1}>{datarow.y}</td>
                            {chartDataSecondaryPoints?.any() &&
                                <td colSpan={1}>{chartDataSecondaryPoints.filter(p => p.id === datarow.id).map(secondaryDataRow => secondaryDataRow.y) ?? ""}</td>
                            }
                        </tr>
                    );
                })}
            </tbody>
        );
    }

    private getColumnTitle(columnTitle: string): string {
        const unitsSeparator = "(";
        if (!this.props.chart.displayUnitInSecondLine || !columnTitle)
            return columnTitle;

        const unitsSeparatorIndex = columnTitle.indexOf(unitsSeparator);
        if (unitsSeparatorIndex > 1 && unitsSeparatorIndex < columnTitle.length - 1) {
            return `${columnTitle.split(unitsSeparator)[0]}\n(${columnTitle.split(unitsSeparator)[1]}`;
        }

        return columnTitle;
    }
}

export { VitalSignsDataTable };
